import React, { useCallback } from 'react';
import { Box } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis
} from 'recharts';
import type { AccountTrendGranularity, AccountTrendResponse } from '../types/accountTrend';
import type { AccountTrendMetric } from '../types/accountTrend';
import { formatMoneyNumber } from '../utils/formatMoney';
import { formatAccountMetricValue } from '../utils/accountMetricFormat';
import { getTodayAnchor } from '../utils/accountDashboardFocus';

interface AccountTrendChartProps {
  data: AccountTrendResponse | null;
  metric: AccountTrendMetric;
  granularity: AccountTrendGranularity;
  focusKey: string | null;
  onFocusBucketClick?: (bucket: { key: string; from: string; to: string }) => void;
}

const FOCUS_FILL = '#1565c0';
const DEFAULT_FILL = '#1976d2';
const FUTURE_FILL = '#90caf9';

const AccountTrendChart: React.FC<AccountTrendChartProps> = ({
  data,
  metric,
  granularity,
  focusKey,
  onFocusBucketClick
}) => {
  const currencyCode = data?.meta.currencyCode ?? '';
  const showBudgetLine = Boolean(data?.meta.showBudgetLine);
  const today = getTodayAnchor();

  const chartData = (data?.buckets ?? []).map((bucket) => ({
    key: bucket.key,
    from: bucket.from,
    to: bucket.to,
    label: bucket.label,
    registered: Math.round(bucket.registered * 100) / 100,
    budgetTarget: bucket.budgetTarget != null ? Math.round(bucket.budgetTarget * 100) / 100 : null,
    isFuture: bucket.from > today,
    isFocused: focusKey != null && bucket.key === focusKey
  }));

  const handleBarClick = useCallback(
    (barEntry: { payload?: (typeof chartData)[number] }) => {
      const bucket = barEntry?.payload;
      if (!bucket || bucket.isFuture || !onFocusBucketClick) {
        return;
      }
      onFocusBucketClick({ key: bucket.key, from: bucket.from, to: bucket.to });
    },
    [onFocusBucketClick]
  );

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        height: 300,
        // Suppress browser SVG focus ring on bar click; focus nav uses control bar.
        '& .recharts-wrapper *:focus:not(:focus-visible)': { outline: 'none' },
        '& .recharts-wrapper svg:focus, & .recharts-wrapper path:focus, & .recharts-wrapper g:focus': {
          outline: 'none'
        }
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="label" interval={granularity === 'day' || granularity === 'week' ? 2 : 0} />
          <YAxis
            tickFormatter={(value) => {
              const num = typeof value === 'number' ? value : Number(value);
              if (metric === 'hours') {
                return num.toFixed(0);
              }
              return formatMoneyNumber(num, currencyCode);
            }}
          />
          <ChartTooltip
            cursor={false}
            formatter={(value, name) => {
              const num = typeof value === 'number' ? value : Number(value);
              if (name === 'Registered' || name === 'Budget target') {
                return formatAccountMetricValue(num, metric, currencyCode);
              }
              return value;
            }}
          />
          <Legend />
          <Bar
            dataKey="registered"
            name="Registered"
            cursor="pointer"
            onClick={handleBarClick}
          >
            {chartData.map((entry) => (
              <Cell
                key={entry.key}
                fill={
                  entry.isFuture
                    ? FUTURE_FILL
                    : entry.isFocused
                      ? FOCUS_FILL
                      : granularity === 'all'
                        ? DEFAULT_FILL
                        : DEFAULT_FILL
                }
                stroke={entry.isFocused ? '#0d47a1' : undefined}
                strokeWidth={entry.isFocused ? 2 : 0}
                style={{ cursor: entry.isFuture ? 'default' : 'pointer', opacity: entry.isFuture ? 0.45 : 1 }}
              />
            ))}
          </Bar>
          {showBudgetLine && (
            <Line
              type="monotone"
              dataKey="budgetTarget"
              name="Budget target"
              stroke="#9c27b0"
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              connectNulls
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default AccountTrendChart;
